import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PNL_LINES, MONTH_SHORT, MONTH_NAMES } from '../constants/wfReportConfig';

// Read-only year grid of the W Factory P&L: the workbook's "PNL- Investor (Yearly)"
// sheet — twelve month columns and a YTD column, with the three ratio rows below.
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const money = (n) => Number(n).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctOf = (v) => (v == null ? '' : `${Number(v).toFixed(2)}%`);

export default function WfYearGrid({ year, onPickMonth, suppliedData, drafts = {}, onEdit, onBlur }) {
  const [loadedData, setData] = useState(null);
  const data = suppliedData || loadedData;
  const [error, setError] = useState('');

  useEffect(() => {
    if (suppliedData) return;
    let live = true;
    setData(null); setError('');
    axios.get(`/api/wf-report/year/${year}`, auth())
      .then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load this year'); });
    return () => { live = false; };
  }, [year, suppliedData]);

  if (error) return <p className="wf-error">{error}</p>;
  if (!data) return <div className="wf-sub">Loading…</div>;

  const cols = [...data.months.map(m => m.pnl), data.ytd];   // 12 months, then YTD
  const cell = (p, pick, kind = 'money') => {
    if (!p) return '';
    const v = pick(p);
    if (kind === 'pct') return pctOf(v);
    return v == null ? '' : money(v);
  };
  const line = (label, pick, cls = '', kind = 'money', testId) => (
    <tr className={cls} key={label}>
      <td className="k">{label}</td>
      {cols.map((p, i) => <td key={i} className={i === 12 ? 'ytd' : ''} data-testid={testId && i === 12 ? testId : undefined}>{cell(p, pick, kind)}</td>)}
    </tr>
  );
  const section = (sec) => PNL_LINES.filter(l => l.section === sec).map(l => onEdit ? (
    <tr key={l.key}><td className="k">{l.label}</td>{data.months.map((m, i) => {
      const raw = drafts[m.month]?.[l.key] ?? m.pnl?.lines[l.key] ?? '';
      return <td key={m.month}><WorksheetCell label={`${sec} · ${l.label} · ${MONTH_NAMES[i]} ${year}`} value={raw}
        disabled={m.status === 'final'} onChange={v => onEdit(m.month, l.key, v)} onBlur={onBlur} /></td>;
    })}<td className="ytd">{money(data.ytd.lines[l.key] || 0)}</td></tr>
  ) : line(l.label, p => p.lines[l.key]));
  const neg = (p) => (p && p.net_profit < 0 ? ' neg' : '');

  return (
    <div className={`wf-year ${onEdit ? 'wf-worksheet' : ''}`}>
      <table>
        <thead>
          <tr>
            <th className="k" />
            {data.months.map(m => (
              <th key={m.month} className={m.status ? '' : 'empty'}>
                <button type="button" className="wf-year-m" onClick={() => onPickMonth(m.month)} disabled={!onEdit && !m.status}>{(onEdit ? MONTH_NAMES : MONTH_SHORT)[Number(m.month.slice(5)) - 1]}</button>
                {m.status && <span className={`wf-pill ${m.status}`}>{m.status}</span>}
              </th>
            ))}
            <th className="ytd">YTD</th>
          </tr>
        </thead>
        <tbody>
          <tr className="group"><td className="k" colSpan={14}>Income</td></tr>
          {section('income')}
          {line('Total Income', p => p.total_income, 'calc')}
          <tr className="group"><td className="k" colSpan={14}>COGS</td></tr>
          {section('cogs')}
          {line('Total COGS', p => p.total_cogs, 'calc')}
          {line('Gross Profit', p => p.gross_profit, 'calc')}
          {line('Gross Margin %', p => p.gross_margin_pct, 'ratio', 'pct')}
          <tr className="group"><td className="k" colSpan={14}>Fixed Cost</td></tr>
          {section('fixed')}
          {line('Total Fixed Cost', p => p.total_fixed, 'calc')}
          <tr className={`calc net${neg(data.ytd)}`}>
            <td className="k">Net Profit</td>
            {cols.map((p, i) => <td key={i} className={`${i === 12 ? 'ytd' : ''}${neg(p)}`} data-testid={i === 12 ? 'wf-year-ytd-net' : undefined}>{cell(p, x => x.net_profit)}</td>)}
          </tr>
          <tr className="spacer"><td colSpan={14} /></tr>
          {line('Marketing %', p => p.marketing_pct, 'ratio', 'pct')}
          {line('Wages %', p => p.wages_pct, 'ratio', 'pct')}
          {line('Net Profit %', p => p.net_profit_pct, 'ratio', 'pct')}
        </tbody>
      </table>
    </div>
  );
}

export function WorksheetCell({ value, disabled, label, onChange, onBlur }) {
  const [focused, setFocused] = useState(false);
  const format = v => v === '' || !Number.isFinite(Number(v)) ? v : money(v);
  const [text, setText] = useState(() => format(value));
  useEffect(() => { if (!focused) setText(value === '' || !Number.isFinite(Number(value)) ? value : money(value)); }, [value, focused]);
  const invalid = value !== '' && !Number.isFinite(Number(value));
  return <input className="wf-cell" aria-label={label} aria-invalid={invalid || undefined} inputMode="decimal" disabled={disabled}
    placeholder="—" value={text}
    onFocus={() => setFocused(true)} onChange={e => { setText(e.target.value); onChange(e.target.value.replace(/,/g, '')); }}
    onBlur={() => { setFocused(false); onBlur(); }} />;
}
